import React, {
  createContext,
  useCallback,
  ReactNode,
  useState,
  useContext,
  useEffect,
} from "react"
import api from "../services/api"

import IUser from "../models/IUsers"

interface AuthState {
  token: string
  user: IUser
}

interface SignInCedentials {
  email: string
  password: string
}

interface AuthContextData {
  user: IUser
  expiresSoon: boolean
  signIn(credentials: SignInCedentials): Promise<void>
  signOut(): void
  updateUser(user: IUser): void
}

interface Props {
  children: ReactNode
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [expiresSoon, setExpiresSoon] = useState(false)
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem("@TeacherControl:token")
    const user = localStorage.getItem("@TeacherControl:user")

    if (token) {
      const tokenData = JSON.parse(atob(token.split(".")[1]))
      const tokenExpiry = tokenData.exp * 1000
      const currentTime = Date.now()

      if (currentTime > tokenExpiry) {
        localStorage.removeItem("@TeacherControl:token")
        localStorage.removeItem("@TeacherControl:user")

        return {} as AuthState
      }
    }

    if (token && user) {
      const parsedUser = JSON.parse(user)
      if (new Date(parsedUser.expiration_date) < new Date()) {
        localStorage.removeItem("@TeacherControl:token")
        localStorage.removeItem("@TeacherControl:user")

        return {} as AuthState
      }

      api.defaults.headers.authorization = `Bearer ${token}`

      return { token, user: JSON.parse(user) }
    }

    return {} as AuthState
  })

  const signIn = useCallback(async ({ email, password }: SignInCedentials) => {
    const response = await api.post("sessions", {
      email,
      password,
    })

    const { token, user } = response.data

    const expirationDate = new Date(user.expiration_date)
    expirationDate.setHours(expirationDate.getHours() + 3)
    const today = new Date()

    const diffInMs = expirationDate.getTime() - today.getTime()
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

    if (diffInDays <= 7) {
      setExpiresSoon(true)
    }

    localStorage.setItem("@TeacherControl:token", token)
    localStorage.setItem("@TeacherControl:user", JSON.stringify(user))

    api.defaults.headers.authorization = `Bearer ${token}`

    setData({ token, user })
  }, [])

  const signOut = useCallback(() => {
    localStorage.removeItem("@TeacherControl:token")
    localStorage.removeItem("@TeacherControl:user")

    setData({} as AuthState)
  }, [])

  const updateUser = useCallback(
    (user: IUser) => {
      localStorage.setItem("@TeacherControl:user", JSON.stringify(user))

      setData({
        token: data.token,
        user,
      })

      const expirationDate = new Date(user.expiration_date)
      expirationDate.setHours(expirationDate.getHours() + 3)
      const today = new Date()

      const diffInMs = expirationDate.getTime() - today.getTime()
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

      if (diffInDays <= 7) {
        setExpiresSoon(true)
      } else {
        setExpiresSoon(false)
      }
    },
    [setData, data.token]
  )

  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("@TeacherControl:token")

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]))
          const tokenExpiry = payload.exp * 1000

          if (Date.now() >= tokenExpiry) {
            signOut()
          }
        } catch (error) {
          console.error("Erro ao decodificar o token, deslogando.", error)
          signOut()
        }
      }
    }, 60 * 1000)

    return () => clearInterval(interval)
  }, [signOut])

  useEffect(() => {
    const interval = setInterval(() => {
      const storedUser = localStorage.getItem("@TeacherControl:user")

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser)

        const expirationDate = new Date(parsedUser.expiration_date)
        expirationDate.setHours(expirationDate.getHours() + 3)

        if (expirationDate < new Date()) {
          localStorage.removeItem("@TeacherControl:token")
          localStorage.removeItem("@TeacherControl:user")
          setData({} as AuthState)
        }
      }
    }, 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user: data.user, expiresSoon, signIn, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext)

  return context
}
